"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { VideoHistoryItem } from "@saas/shared/history/types";
import {
	getVideoDisplayPrompt,
	getVideoEffect,
	useVideoHomeRouter,
} from "@saas/shared/history/video-home";
import { getVideoRowModelLabel } from "@saas/shared/history/video-model-label";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import {
	DownloadIcon,
	EditIcon,
	RefreshCwIcon,
	StarIcon,
	Trash2Icon,
	VideoIcon,
	XIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type VideoGeneration = {
	id: string;
	prompt: string;
	model: string;
	resolution: string;
	duration: number;
	status: string;
	videoUrl: string | null;
	inputImageUrl: string;
	inputImageUrls?: string[];
	createdAt: string;
	metadata?: VideoHistoryItem["metadata"];
};

interface VideoDetailModalProps {
	isOpen: boolean;
	onClose: () => void;
	video: VideoGeneration;
	onReprompt?: () => void;
	onRegenerate?: () => void;
	/** 收藏和删除的状态、写接口都在 AssetGrid 那边 */
	isFavorite?: boolean;
	onToggleFavorite?: () => void;
	onDelete?: () => void;
}

export function VideoDetailModal({
	isOpen,
	onClose,
	video,
	onReprompt,
	onRegenerate,
	isFavorite,
	onToggleFavorite,
	onDelete,
}: VideoDetailModalProps) {
	const t = useTranslations();
	const [isPromptExpanded, setIsPromptExpanded] = useState(false);
	const effect = getVideoEffect(video);
	const prompt = getVideoDisplayPrompt(video);
	// 不是本页出的片回它出生的页面去做，同 VideoHistoryCard
	const routeHome = useVideoHomeRouter();

	// 长 prompt 先收到 6 行，免得移动端整页被撑得太长
	const isPromptLong = prompt.length > 200;

	// 换一条记录重新打开时别把上一条的展开状态带过来
	useEffect(() => {
		if (isOpen) {
			setIsPromptExpanded(false);
		}
	}, [isOpen]);

	// 老记录只有 inputImageUrl 这一列，新记录用数组
	const referenceImages =
		video.inputImageUrls && video.inputImageUrls.length > 0
			? video.inputImageUrls
			: video.inputImageUrl
				? [video.inputImageUrl]
				: [];

	const handleReprompt = () => {
		if (routeHome(video, "reprompt")) {
			onClose();
			return;
		}
		if (onReprompt) {
			onReprompt();
			onClose();
		} else {
			toast.info(t("assets.detail.repromptUnavailable"));
		}
	};

	const handleRegenerate = () => {
		if (routeHome(video, "regenerate")) {
			onClose();
			return;
		}
		if (onRegenerate) {
			onRegenerate();
			onClose();
		} else {
			toast.info(t("assets.detail.regenerateUnavailable"));
		}
	};

	const handleDownload = async (videoUrl: string) => {
		try {
			const proxyUrl = `/api/proxy-download?url=${encodeURIComponent(videoUrl)}`;
			const a = document.createElement("a");
			a.href = proxyUrl;
			document.body.appendChild(a);
			a.click();
			setTimeout(() => {
				document.body.removeChild(a);
			}, 100);
		} catch (error) {
			console.error("Error downloading video:", error);
			toast.error("Failed to download video");
		}
	};

	const formatDateTime = (date: Date) => {
		const locale =
			typeof navigator !== "undefined" && navigator.language
				? navigator.language
				: "en-US";
		return new Intl.DateTimeFormat(locale, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		}).format(date);
	};

	return (
		<DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				<DialogPrimitive.Content
					className={cn(
						"fixed inset-0 z-50 flex flex-col md:flex-row",
						// 移动端整页一条滚动轴，桌面端还是左图右栏各自不动
						"bg-card overflow-y-auto outline-none md:overflow-hidden",
						"data-[state=closed]:animate-out data-[state=open]:animate-in",
						"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
					)}
					// 没有描述文字，明确告诉 Radix 别再找 aria-describedby
					aria-describedby={undefined}
				>
					{/* Radix 要求 Content 里必须有 Title，否则读屏用户拿不到弹窗名字。
					    这个弹窗的主体就是视频本身，没有可见的标题文字，所以藏起来 */}
					<DialogPrimitive.Title className="sr-only">
						{t("assets.detail.title")}
					</DialogPrimitive.Title>
					{/* Video player - top on mobile, left on desktop */}
					{/* min-h 保底，内容不够长时 flex-1 再把播放区撑满剩余空间 */}
					<div className="relative flex-1 min-h-[52dvh] flex items-center justify-center bg-neutral-100 p-3 dark:bg-neutral-950 md:min-h-0 md:p-8">
						{/* 移动端靠右，桌面端右上被右侧详情面板占着所以靠左。用 flex 对齐而
						    不是 left/right 互相抵消——两边都有值时按钮会被拉成一整条 */}
						<div className="pointer-events-none absolute inset-x-3 top-3 z-10 flex justify-end md:inset-x-4 md:top-4 md:justify-start">
							<button
								type="button"
								onClick={onClose}
								className="pointer-events-auto rounded-full bg-black/70 p-2 text-white shadow-lg transition-all hover:bg-black dark:bg-white/90 dark:text-black dark:hover:bg-white"
								aria-label={t("assets.detail.close")}
							>
								<XIcon className="h-5 w-5" />
							</button>
						</div>

						{video.videoUrl ? (
							/* controlsList 藏掉原生 ⋮ 菜单：它的下载会绕过
							   /api/proxy-download 直接下原始 URL（文件名是一串哈希），
							   而且跟侧栏的 Download 重复；倍速和画中画对几秒的片子也没意义 */
							/* biome-ignore lint/a11y/useMediaCaption: AI 生成的片段没有字幕轨 */
							<video
								src={`${video.videoUrl}#t=0.001`}
								controls
								controlsList="nodownload noplaybackrate noremoteplayback"
								disablePictureInPicture
								autoPlay
								playsInline
								preload="metadata"
								className="max-h-full max-w-full w-auto h-auto object-contain rounded-lg"
							/>
						) : (
							<p className="text-sm text-muted-foreground">
								{t("assets.videoFailed")}
							</p>
						)}
					</div>

					{/* Details panel - bottom on mobile, right on desktop */}
					<div className="w-full shrink-0 flex flex-col bg-card border-t md:w-[320px] md:min-h-0 md:border-t-0 md:border-l">
						{/* Primary action bar — download is the most frequent action */}
						<div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-4 md:px-6 md:pt-6">
							<Button
								variant="outline"
								className="h-10 gap-2 border-border text-foreground hover:bg-muted"
								disabled={!video.videoUrl}
								onClick={() =>
									video.videoUrl &&
									handleDownload(video.videoUrl)
								}
							>
								<DownloadIcon className="h-4 w-4" />
								{t("assets.buttons.download")}
							</Button>
							<div className="flex items-center gap-1">
								{onToggleFavorite ? (
									<Button
										variant="ghost"
										size="icon"
										className={cn(
											"h-10 w-10 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground",
											isFavorite && "text-yellow-500",
										)}
										aria-label={t(
											"assets.buttons.favorite",
										)}
										aria-pressed={isFavorite}
										onClick={onToggleFavorite}
									>
										<StarIcon
											className={cn(
												"h-4 w-4",
												isFavorite && "fill-current",
											)}
										/>
									</Button>
								) : null}
								{onDelete ? (
									<Button
										variant="ghost"
										size="icon"
										className="h-10 w-10 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
										aria-label={t(
											"assets.buttons.deleteImage",
										)}
										onClick={onDelete}
									>
										<Trash2Icon className="h-4 w-4" />
									</Button>
								) : null}
							</div>
						</div>

						{/* Kind badge + create time */}
						<div className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3 md:px-6 md:py-4">
							<span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
								{effect ? (
									<StarIcon className="h-4 w-4" />
								) : (
									<VideoIcon className="h-4 w-4" />
								)}
								{effect
									? `EFFECT · ${t(effect.titleKey)}`
									: t("assets.detail.videoBadge")}
							</span>
							<span className="text-xs text-muted-foreground">
								{formatDateTime(new Date(video.createdAt))}
							</span>
						</div>

						{/* Details content */}
						<div className="px-4 py-4 space-y-6 md:flex-1 md:overflow-y-auto md:px-6 md:py-6 md:space-y-8">
							{/* Reference Image */}
							{referenceImages.length > 0 ? (
								<div className="space-y-2">
									<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
										{t("assets.detail.referenceImage")}
									</div>
									<div className="flex gap-2 flex-wrap">
										{referenceImages.map((url, index) => (
											<img
												key={index}
												src={url}
												alt={`Reference ${index + 1}`}
												className="h-20 w-20 rounded-lg object-cover border bg-muted"
											/>
										))}
									</div>
								</div>
							) : null}

							{/* Prompt */}
							<div className="space-y-2">
								{prompt ? (
									<>
										<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
											{t("assets.detail.prompt")}
										</div>
										<p
											className={cn(
												"text-sm text-foreground leading-relaxed bg-muted/30 p-3 rounded-lg border border-transparent hover:border-border transition-colors",
												isPromptLong &&
													!isPromptExpanded &&
													"line-clamp-6",
											)}
										>
											{prompt}
										</p>
									</>
								) : null}
								{isPromptLong ? (
									<button
										type="button"
										onClick={() =>
											setIsPromptExpanded(
												(expanded) => !expanded,
											)
										}
										className="text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
									>
										{isPromptExpanded
											? t("assets.detail.showLess")
											: t("assets.detail.showMore")}
									</button>
								) : null}
								{/* 规格拼成一行，缺的项直接不显示 */}
								<p className="text-xs text-muted-foreground">
									{[
										getVideoRowModelLabel(video),
										video.resolution,
										t("assets.detail.durationSeconds", {
											seconds: video.duration,
										}),
									]
										.filter(Boolean)
										.join("  |  ")}
								</p>
							</div>
						</div>

						{/* Action buttons */}
						<div className="shrink-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:p-6 md:pb-6">
							<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
								{t("assets.detail.more")}
							</div>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									className="flex-1 h-11 gap-2 px-4 border-border text-foreground hover:bg-muted"
									onClick={handleReprompt}
								>
									<EditIcon className="h-4 w-4" />
									{t("assets.detail.reprompt")}
								</Button>
								<Button
									variant="outline"
									className="flex-1 h-11 gap-2 px-4 border-border text-foreground hover:bg-muted"
									onClick={handleRegenerate}
								>
									<RefreshCwIcon className="h-4 w-4" />
									{t("assets.detail.regenerate")}
								</Button>
							</div>
						</div>
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
